import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Tag, X } from "lucide-react";
import { toast } from "sonner";
import { handleImgError } from "../lib/imageFallback";

const AUTH_PAGES = ["/login", "/register"];
const isBlockedPath = (pathname) => AUTH_PAGES.includes(pathname) || pathname.startsWith("/admin");

// Small popup: "corner" (bottom-right card), "bar" (full-width bottom bar) or "header" (full-width top bar).
// When linked to a product, renders a compact "mini" version of the big modal (thumbnail + price + discount badge).
const SmallPopup = ({ popup, onDismiss, onCta }) => {
  const isBar = popup.popup_type === "bar";
  const isHeader = popup.popup_type === "header";
  const isFullWidth = isBar || isHeader;
  const bg = popup.background_color || "#FFFFFF";
  const isLight = bg.toUpperCase() === "#FFFFFF";
  const textCls = isLight ? "text-[#0F172A]" : "text-white";
  const mutedCls = isLight ? "text-[#0F172A]/65" : "text-white/80";
  const faintCls = isLight ? "text-[#0F172A]/50" : "text-white/60";
  const closeBtnCls = isLight ? "bg-black/10 hover:bg-black/15 text-[#0F172A]" : "bg-white/15 hover:bg-white/25 text-white";
  const ctaCls = isLight ? "bg-[#0F172A] text-white hover:bg-[#1E293B]" : "bg-white text-[#0F172A] hover:bg-white/90";
  const borderCls = isLight ? "border border-[#E2E8F0]" : "";
  const prod = popup.product;
  const isAuction = prod?.sale_type === "auction";
  const displayPrice = prod ? (isAuction ? (prod.current_bid || prod.price) : (prod.promo_price || prod.price)) : null;
  const thumb = popup.image_url;

  return (
    <div
      className={
        isHeader
          ? `fixed top-0 inset-x-0 z-[100] pointer-events-auto animate-in slide-in-from-top duration-300 ${borderCls}`
          : isBar
          ? `fixed bottom-0 inset-x-0 z-[100] pointer-events-auto animate-in slide-in-from-bottom duration-300 ${borderCls}`
          : `fixed bottom-4 right-4 z-[100] pointer-events-auto w-[290px] rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300 ${borderCls}`
      }
      style={{ backgroundColor: bg }}
      data-testid="small-promo-popup"
    >
      <div className={isFullWidth ? "max-w-3xl mx-auto px-4 py-2.5 flex items-center gap-3" : "p-3.5 relative flex gap-3"}>
        <button
          onClick={onDismiss}
          className={`${isFullWidth ? "order-last flex-shrink-0" : "absolute top-2 right-2"} w-6 h-6 rounded-full ${closeBtnCls} flex items-center justify-center transition-colors z-10`}
          data-testid="small-popup-close"
          aria-label="Cerrar"
        >
          <X size={13} />
        </button>

        {thumb && (
          <div className={`relative flex-shrink-0 rounded-xl overflow-hidden ${isFullWidth ? "w-10 h-10" : "w-16 h-16"}`}>
            <img src={thumb} alt="" onError={handleImgError} className="w-full h-full object-cover" />
            {prod?.promo_discount_percent > 0 && !isFullWidth && (
              <span className="absolute top-0.5 left-0.5 bg-red-500 text-white text-[9px] font-extrabold px-1 rounded" data-testid="small-popup-discount-badge">
                -{prod.promo_discount_percent}%
              </span>
            )}
          </div>
        )}

        <div className={isFullWidth ? "flex-1 min-w-0 flex items-center gap-3" : "flex-1 min-w-0 pr-4"}>
          <div className="min-w-0">
            <h3 className={`${textCls} font-bold leading-tight ${isFullWidth ? "text-xs" : "text-sm"} line-clamp-1 ${popup.use_marquee ? "animate-marquee inline-block" : ""}`} data-testid="small-popup-title">
              {popup.title}
            </h3>
            {popup.message && (
              <p className={`${mutedCls} text-xs mt-0.5 ${isFullWidth ? "line-clamp-1" : "leading-snug line-clamp-2"}`} data-testid="small-popup-message">
                {popup.message}
              </p>
            )}
            {prod && (
              <div className="flex items-baseline gap-1.5 mt-1" data-testid="small-popup-price">
                <span className={`${textCls} font-extrabold text-sm`}>${displayPrice?.toLocaleString()}</span>
                {prod.promo_price && <span className={`${faintCls} text-[11px] line-through`}>${prod.price?.toLocaleString()}</span>}
                {isAuction && <span className={`${mutedCls} text-[10px]`}>puja actual</span>}
              </div>
            )}
          </div>
        </div>

        {isFullWidth ? (
          <Button
            onClick={onCta}
            size="sm"
            className={`flex-shrink-0 rounded-full ${ctaCls} text-xs font-bold`}
            data-testid="small-popup-cta"
          >
            {popup.button_text || "Ver más"}
          </Button>
        ) : null}
      </div>
      {!isFullWidth && (
        <div className="px-3.5 pb-3.5 -mt-1">
          <Button
            onClick={onCta}
            size="sm"
            className={`w-full rounded-full ${ctaCls} text-xs font-bold`}
            data-testid="small-popup-cta"
          >
            {popup.button_text || "Ver más"}
          </Button>
        </div>
      )}
    </div>
  );
};

export const PromoPopup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [popup, setPopup] = useState(null);
  const [open, setOpen] = useState(false);
  const [smallPopup, setSmallPopup] = useState(null);
  const [smallOpen, setSmallOpen] = useState(false);

  // Seen state is tracked per account (or guest) on this device, so a new popup
  // always shows at least once, and a different account on the same device/PWA
  // also sees it at least once even if another account already dismissed it.
  const seenKey = (popupId) => `popup_seen_${popupId}_${user?.user_id || "guest"}`;

  useEffect(() => {
    if (isBlockedPath(location.pathname)) return;
    let cancelled = false;
    let timer = null;
    let smallTimer = null;
    axios.get(`${API}/popups/active`)
      .then((res) => {
        if (cancelled) return;
        const p = res.data.popup;
        const sp = res.data.small_popup;
        const willShowTutorial = !!user && !user.is_admin && !localStorage.getItem("ibiid_tutorial_seen");
        if (p && !localStorage.getItem(seenKey(p.popup_id))) {
          setPopup(p);
          const tryOpen = () => {
            if (cancelled) return;
            if (willShowTutorial && !localStorage.getItem("ibiid_tutorial_seen")) {
              timer = setTimeout(tryOpen, 500);
              return;
            }
            setOpen(true);
          };
          timer = setTimeout(tryOpen, willShowTutorial ? 1800 : 1200);
        }
        if (sp && !localStorage.getItem(seenKey(sp.popup_id))) {
          setSmallPopup(sp);
          smallTimer = setTimeout(() => { if (!cancelled) setSmallOpen(true); }, 2500);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; if (timer) clearTimeout(timer); if (smallTimer) clearTimeout(smallTimer); };
  }, [user?.user_id, location.pathname]);

  // If the user navigates to an auth page while the popup is open, close it
  // without marking it as seen so it can still show later on other pages.
  useEffect(() => {
    if (isBlockedPath(location.pathname)) { setOpen(false); setSmallOpen(false); }
  }, [location.pathname]);

  const dismiss = () => {
    if (popup) localStorage.setItem(seenKey(popup.popup_id), "1");
    setOpen(false);
  };

  const dismissSmall = () => {
    if (smallPopup) localStorage.setItem(seenKey(smallPopup.popup_id), "1");
    setSmallOpen(false);
  };

  const goTo = (p) => {
    if (p?.action_url) {
      if (p.action_url.startsWith("http")) {
        window.open(p.action_url, "_blank");
      } else {
        navigate(p.action_url);
      }
      return;
    }
    if (p?.product_id) navigate(`/product/${p.product_id}`);
  };

  const handleCta = () => { dismiss(); goTo(popup); };
  const handleSmallCta = () => { dismissSmall(); goTo(smallPopup); };

  const copyCode = () => {
    navigator.clipboard?.writeText(popup.discount_code);
    toast.success("Código copiado");
  };

  const prod = popup?.product;
  const isAuction = prod?.sale_type === "auction";
  const displayPrice = prod
    ? (isAuction ? (prod.current_bid || prod.price) : (prod.promo_price || prod.price))
    : null;

  return (
    <>
      {popup && (
        <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
          <DialogContent
            className="max-w-sm w-[92vw] rounded-3xl p-0 overflow-hidden border-0 bg-white"
            data-testid="promo-popup"
            onInteractOutside={(e) => {
              // Don't close the modal when the user interacts with the small
              // corner/bar popup that is rendered simultaneously.
              const target = e.detail?.originalEvent?.target;
              if (target && target.closest && target.closest('[data-testid=small-promo-popup]')) {
                e.preventDefault();
              }
            }}
          >
            <DialogTitle className="sr-only">{popup.title}</DialogTitle>
            <DialogDescription className="sr-only">{popup.message || "Oferta promocional"}</DialogDescription>
            {popup.image_url && (
              <div className="relative">
                <img src={popup.image_url} alt={popup.title} onError={handleImgError} className={`w-full object-cover ${prod ? "aspect-square" : "aspect-[3/2]"}`} />
                {prod && (
                  <>
                    <span className={`absolute top-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full text-white shadow ${isAuction ? "bg-emerald-500" : "bg-orange-500"}`} data-testid="promo-popup-type-badge">
                      {isAuction ? "SUBASTA" : "VENTA DIRECTA"}
                    </span>
                    {prod.promo_discount_percent > 0 && (
                      <span className="absolute top-3 right-10 text-[11px] font-extrabold px-2.5 py-1 rounded-full bg-red-500 text-white shadow" data-testid="promo-popup-discount-badge">
                        -{prod.promo_discount_percent}%
                      </span>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent pt-10 pb-3 px-4">
                      <h2 className="text-white font-bold text-lg leading-tight line-clamp-2" data-testid="promo-popup-title">{popup.title}</h2>
                      <div className="flex items-baseline gap-2 mt-1">
                        <span className="text-white text-2xl font-extrabold" data-testid="promo-popup-price">${displayPrice?.toLocaleString()}</span>
                        {prod.promo_price && (
                          <span className="text-white/70 text-sm line-through">${prod.price?.toLocaleString()}</span>
                        )}
                        {isAuction && <span className="text-white/80 text-xs font-medium">puja actual</span>}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="p-5 space-y-3">
              {!prod && <h2 className="text-lg font-bold text-[#0F172A]" data-testid="promo-popup-title">{popup.title}</h2>}
              <p className="text-sm text-[#374151]">{popup.message}</p>
              {popup.discount_code && (
                <button
                  onClick={copyCode}
                  className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-[#F97316] rounded-xl py-2 text-[#F97316] font-mono font-bold hover:bg-orange-50 transition-colors"
                  data-testid="promo-popup-code"
                >
                  <Tag size={15} /> {popup.discount_code}
                </button>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 rounded-full" onClick={dismiss} data-testid="promo-popup-close">
                  Cerrar
                </Button>
                <Button className={`flex-1 rounded-full text-white ${isAuction ? "bg-emerald-500 hover:bg-emerald-600" : "bg-[#F97316] hover:bg-[#EA580C]"}`} onClick={handleCta} data-testid="promo-popup-cta">
                  {popup.button_text || (isAuction ? "Participar" : popup.product_id ? "Ver producto" : "Ver más")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {smallPopup && smallOpen && (
        <SmallPopup popup={smallPopup} onDismiss={dismissSmall} onCta={handleSmallCta} />
      )}
    </>
  );
};

export default PromoPopup;
